import { forwardRef } from "react";
import { ChevronDown } from "lucide-react";

/**
 * SelectInput — wraps a native <select> with the app's visual language.
 * Uses the native element deliberately (not a custom listbox) for full
 * accessibility and mobile picker support out of the box, matching the
 * "don't over-engineer the MVP" principle from the architecture spec.
 *
 * @param {{ value: string, label: string }[]} options
 */
const SelectInput = forwardRef(function SelectInput(
  { label, options = [], placeholder = "Tanlang", error, className = "", id, ...rest },
  ref
) {
  const selectId = id || rest.name;

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={selectId}
          className="block text-caption uppercase tracking-wide text-brand-muted mb-1.5"
        >
          {label}
        </label>
      )}
      <div
        className={[
          "relative flex items-center rounded-lg bg-white border h-12 px-3.5",
          error ? "border-brand-danger" : "border-brand-line",
        ].join(" ")}
      >
        <select
          ref={ref}
          id={selectId}
          className={[
            "flex-1 min-w-0 bg-transparent outline-none appearance-none text-body-md text-brand-ink pr-6",
            className,
          ].join(" ")}
          {...rest}
        >
          <option value="" disabled hidden>
            {placeholder}
          </option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <ChevronDown
          size={16}
          className="absolute right-3.5 text-brand-muted pointer-events-none"
        />
      </div>
      {error && <p className="mt-1.5 text-body-sm text-brand-danger">{error}</p>}
    </div>
  );
});

export default SelectInput;
